import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Breadcrumb.scss';
import { useTranslation } from "react-i18next";
import { honeBreadcrumbIconUrlNew, LOCATION_BREADCRUMB } from "../../../helper/constants";
import { useDispatch, useSelector } from 'react-redux';
import { FacetFilterActions } from "../../../storage/reducers/facetFilterReducer";
import {
        breadcrumbRoutes, 
        addNewBreadcrumbItem, 
        useBreadcrumbData, 
        redirection, 
        deleteBreadcrumbCurrentPageNameFromSessionStorage, 
        handleOpenRequestPublicationModal,        
      } from './breadcrumbHelper';
import { setMenuSelection } from '../../../storage/reducers/menuSelectorReducer';
import ConfirmModalPopup from '../TechpubLinks/ConfirmModalPopup';

const Breadcrumb: React.FC = () => {
  const { navigate, subCategoryNameVirtualClassrooms, itemName, subCategoryNameEngines } = useBreadcrumbData();
  const routesWithParams = ['viewcollection', 'temprevisions', 'relatedpub', 'subscription_request'];
  const noAheadItemForNextPaths = ['hangardetailedview'];
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);
  const { facetObj, selectedFactes, emptySearch } = useSelector((state: any) => state.filteredFacets);
  const [showRequestPublicationModal, setShowRequestPublicationModal] = useState(false);
  const [modalNavigationPath, setModalNavigationPath] = useState("");

  const findBreadcrumbName = (path: string) => {
    for (const config of breadcrumbRoutes) {
      const regex = new RegExp(`^${config.path.replace(/:\w+/g, '\\w+')}$`);
      if (regex.test(path)) {
        return config.name;
      }
    }
    return null;
  };

  const replaceBreadcrumbItemName = (name: string, index: number, pathnames: string[]) => {
    if(name === "publicationSearch"){
      return "";
    }
    // SPECIAL CASES FOR BREADCRUMB, REPLACEMENT OF ITEMS AND LINKS IN DEEPLINKING
    // ---------------------------------------
    const breadCrumbSearchResultPageName = window.sessionStorage.getItem("breadCrumbSearchResultPageName") || undefined;
    const hasCommonPathName = pathnames.some(item => routesWithParams.includes(item));
    if(breadCrumbSearchResultPageName?.includes("/hangardetailedview") && hasCommonPathName){
      return "";
    }
    if((breadCrumbSearchResultPageName === "/virtualclassroomsDetailList" || breadCrumbSearchResultPageName === "/enginesList" ) && hasCommonPathName){
      return "";
    }

    //SWITCH BREADCRUMB IF PATHNAME HAS PARAMETER 
    // --------------------------------------- 
    if(routesWithParams.includes(name) && index < pathnames.length - 1){
      return pathnames[index + 1];
    }
    if(routesWithParams.includes(pathnames[index - 1]) && index === pathnames.length - 1){
      // return findBreadcrumbName(`/${pathnames[index - 1]}`);
      return "...";
    }
    // ---------------------------------------
    
    const fullPath = `/${pathnames.slice(0, index + 1).join('/')}`;
    const breadcrumbName = findBreadcrumbName(fullPath);
    if (breadcrumbName) {
      return breadcrumbName;
    }
    
    // RETURN EMPTY FOR THOSE AHEAD BREADCRUMB ITEM SPECIFICT PATHS
    // ---------------------------------------
    if (noAheadItemForNextPaths.includes(pathnames[pathnames.length - 2]) && index === pathnames.length - 1) {
      return ""; // Do not show the breadcrumb item
    }
    // ---------------------------------------  
    
    return name; // Return the original name for other cases
  };

  const resetLandingPageFromBreadcrumb = () => {
      navigate("/");
      deleteBreadcrumbCurrentPageNameFromSessionStorage(); 
      dispatch(
        FacetFilterActions.getSelectedFilterData({
          facetObjectVal: "",
          facetPayloadVal: "",
          redirecTechLink: "/publicationSearch",
        })
      );
      dispatch(
        setMenuSelection({
          currentMenuSelection: "/publicationSearch",
          currentMenuTitle: ""
        })
      );
  };

  const handleBreadcrumbValidation = (navValue: string, onSuccess: () => void) => {
    const openModal = handleOpenRequestPublicationModal();
    if (openModal) {
      setShowRequestPublicationModal(true);
      setModalNavigationPath(navValue);
    } else {
      onSuccess();
    }
  };

  return (
    <>
      <ConfirmModalPopup
        show={showRequestPublicationModal}
        setShowPopup={setShowRequestPublicationModal}
        navigationbarVal={modalNavigationPath}
        actionInsteadOfNavigate={resetLandingPageFromBreadcrumb}
      />
      <div className="breadcrumbContainer">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            {/* BREADCRUMB HOME ICON ITEM */}
            <li
              className="breadcrumb-item"
              onClick={() =>
                deleteBreadcrumbCurrentPageNameFromSessionStorage()
              }
            >
              <a href={honeBreadcrumbIconUrlNew}>
                Home
              </a>
            </li>
            {/* BREADCRUMB HOME TEXT */}
            <li className="breadcrumb-item homeText">
              <a
                onClick={() => {
                  handleBreadcrumbValidation("", () => {
                    deleteBreadcrumbCurrentPageNameFromSessionStorage();
                    resetLandingPageFromBreadcrumb();
                  });
                }}
              >
                {t(LOCATION_BREADCRUMB.PUBLICATIONS_SEARCH)}
              </a>
            </li>

            {pathnames.length === 0 &&
              (selectedFactes?.queryString ||
                selectedFactes?.pageable ||
                emptySearch) && (
                <li className="breadcrumb-item">
                  <a>{t(LOCATION_BREADCRUMB.SEARCH_RESULTS)}</a>
                </li>
              )}
            {/* BREADCRUMBS DEPENDING THE CURRENT PATH NAME */}
            {pathnames.map((value, index) => {
              const to = `/${pathnames.slice(0, index + 1).join("/")}`;
              const breadcrumbName = replaceBreadcrumbItemName(
                value,
                index,
                pathnames
              );
              if (breadcrumbName === "") return null; // Skip empty breadcrumb items
              return routesWithParams.includes(
                pathnames[pathnames.length - 2]
              ) && breadcrumbName === pathnames[pathnames.length - 1] ? (
                <li
                  key={to}
                  className="breadcrumb-item"
                  onClick={() => {
                    const navTo = window.sessionStorage.getItem("breadCrumbSearchResultPageName") || "";
                    handleBreadcrumbValidation(navTo, () => {
                      navigate(navTo);
                      deleteBreadcrumbCurrentPageNameFromSessionStorage();
                    });
                  }}
                >
                  <a>{t(LOCATION_BREADCRUMB.SEARCH_RESULTS)}</a>
                </li>
              ) : (
                <li key={to} className="breadcrumb-item">
                  <Link to={redirection(to, breadcrumbName || "")}>
                    {breadcrumbName}
                  </Link>
                </li>
              );
            })}
            {/* ADDITIONAL BREADCRUMBS FOR SPECIFICT PATHS LIKE VIRTUAL CLASSROOMS OR ENGINES, OR SEARCH RESULTS  (it can be one or more than one breadcrumb returned)*/}
            {addNewBreadcrumbItem(
              pathnames[pathnames.length - 1],
              pathnames.length - 1,
              navigate,
              subCategoryNameVirtualClassrooms,
              itemName,
              subCategoryNameEngines,
              window.sessionStorage.getItem("breadCrumbSearchResultPageName") || undefined,
              handleBreadcrumbValidation
            )}
          </ol>
        </nav>
      </div>
    </>
  );
};
export default Breadcrumb;
